import React from 'react';
import { CloseOutlined } from '@ant-design/icons';
import '../tlgreenwave.css';

// Traffic light icon component
const TrafficLightIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="7" y="2" width="10" height="20" rx="2" fill="#333" />
    <circle cx="12" cy="6" r="2.5" fill="#ff4d4f" />
    <circle cx="12" cy="12" r="2.5" fill="#faad14" />
    <circle cx="12" cy="18" r="2.5" fill="#52c41a" />
  </svg>
);

// Arrow icon component
const ArrowIcon = () => (
  <svg width="20" height="12" viewBox="0 0 20 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M1 6H19M19 6L14 1M19 6L14 11" stroke="#999" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/**
 * ControlloOndaVerdeCard Component
 * Displays the results of the green wave calculation.
 *
 * Sections:
 * 1. Distanze e Velocità – link pair distances and speeds
 * 2. Dettaglio Semafori – per semaforo: durata verde, giallo, rosso, lunghezza attr., offset calcolato, nuovo inizio verde
 * 3. Intervalli di Onda Verde – green wave intervals
 */
function ControlloOndaVerdeCard({
  visible,
  onClose,
  data,
}) {
  if (!visible || !data) return null;

  const resultType = data.result?.type;
  const finalResult = resultType === 'global' ? data.result?.global_result : data.result?.corridor_result;
  const offsets = finalResult?.offsets || {};
  const corridor = data.signals?.map(s => s.id) || data.requestParams?.semafori?.map(s => s.id) || [];
  const score = finalResult?.score;
  const requestParams = data.requestParams;

  // Build semafori details for display
  const risultati = corridor.map(id => {
    // Check both requestParams and the API response signals array
    const queryList = requestParams?.signals || data.signals || [];
    const inputSemaforo = queryList.find(s => s.id === id);
    const computedOffset = offsets[id] || 0;
    const greenStart = inputSemaforo?.green_start || 0;
    const newGreenStart = greenStart + computedOffset;

    return {
      id: id,
      semaforo: inputSemaforo?.nome || id,
      durata_verde: inputSemaforo?.green_duration || 30, // Updated name
      durata_giallo: inputSemaforo?.yellow_time || 3,
      durata_rosso: inputSemaforo?.red_time || 30,
      lunghezza_attraversamento: inputSemaforo?.crossing_length_m || 10, // Updated name
      offset_calcolato: computedOffset.toFixed(2),
      nuovo_inizio_verde: newGreenStart.toFixed(2),
    };
  });

  // Build link pairs for distance/speed display
  const linkPairs = [];
  const queryLinks = data.links || requestParams?.links || [];
  const querySignals = requestParams?.signals || data.signals || [];
  
  for (let i = 0; i < corridor.length - 1; i++) {
    const fromId = corridor[i];
    const toId = corridor[i + 1];
    const fromSem = querySignals.find(s => s.id === fromId);
    const toSem = querySignals.find(s => s.id === toId);
    const linkObj = queryLinks.find(l => l.from_signal === fromId && l.to_signal === toId);

    linkPairs.push({
      from: fromSem?.nome || fromId,
      to: toSem?.nome || toId,
      distanza: linkObj?.distance_m || 0,
      velocita: linkObj?.speed_kmh || 50,
    });
  }

  return (
    <div className="tlgw-results-card">
      {/* Header */}
      <div className="tlgw-results-header">
        <div className="tlgw-results-title-group">
          <h3 className="tlgw-results-title">Onda Verde</h3>
          {score !== undefined && (
            <div className="tlgw-results-score-badge">
              <span className="tlgw-results-score-label">Punteggio</span>
              <span className="tlgw-results-score-value">{score.toFixed(2)}</span>
            </div>
          )}
        </div>
        <button
          className="tlgw-results-close"
          onClick={onClose}
          title="Chiudi"
        >
          <CloseOutlined />
        </button>
      </div>

      <div className="tlgw-results-body">
        {/* Mode info if available */}
        {resultType && (
          <div className="tlgw-results-mode-info">
            <span className="tlgw-results-mode-dot" />
            Modalità: <strong>{resultType === 'global' ? 'Ottimizzazione Globale' : resultType}</strong>
          </div>
        )}

        {/* Distanze e Velocità Section (replaces old Differenza Offset) */}
        {linkPairs.length > 0 && (
          <div className="tlgw-results-section">
            <h4 className="tlgw-results-section-title">Distanze e Velocità</h4>
            <div className="tlgw-results-coppie-list">
              {linkPairs.map((pair, index) => (
                <div key={index} className="tlgw-results-coppia-item">
                  <div className="tlgw-results-coppia-path">
                    <span className="tlgw-results-id-pill">{pair.from}</span>
                    <ArrowIcon />
                    <span className="tlgw-results-id-pill">{pair.to}</span>
                  </div>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <div className="tlgw-results-coppia-offset-tag">
                      {pair.distanza}m
                    </div>
                    <div className="tlgw-results-coppia-offset-tag" style={{ background: 'rgba(140, 116, 255, 0.15)', color: '#8C74FF' }}>
                      {pair.velocita} km/h
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Dettaglio Semafori Section */}
        <div className="tlgw-results-section">
          <h4 className="tlgw-results-section-title">Dettaglio Semafori</h4>
          <div className="tlgw-results-semafori-list">
            {risultati.map((semaforo, index) => (
              <div key={index} className="tlgw-results-semaforo-premium-card">
                <div className="tlgw-results-semaforo-header">
                  <div className="tlgw-results-semaforo-icon-wrapper">
                    <TrafficLightIcon />
                  </div>
                  <div className="tlgw-results-semaforo-info">
                    <div className="tlgw-results-semaforo-name">{semaforo.semaforo}</div>
                  </div>
                </div>

                <div className="tlgw-results-main-timing">
                  <div className="tlgw-timing-item">
                    <div className="tlgw-timing-label">Offset Calcolato</div>
                    <div className="tlgw-timing-value highlighted">{semaforo.offset_calcolato}s</div>
                  </div>
                  <div className="tlgw-timing-item">
                    <div className="tlgw-timing-label">Nuovo Inizio Verde</div>
                    <div className="tlgw-timing-value" style={{ color: '#A1E430' }}>{semaforo.nuovo_inizio_verde}s</div>
                  </div>
                </div>

                <div className="tlgw-results-divider" />

                <div className="tlgw-results-grid">
                  <div className="tlgw-grid-item">
                    <span className="tlgw-grid-label">Verde</span>
                    <span className="tlgw-grid-value green">{semaforo.durata_verde}s</span>
                  </div>
                  <div className="tlgw-grid-item">
                    <span className="tlgw-grid-label">Giallo</span>
                    <span className="tlgw-grid-value yellow">{semaforo.durata_giallo}s</span>
                  </div>
                  <div className="tlgw-grid-item">
                    <span className="tlgw-grid-label">Rosso</span>
                    <span className="tlgw-grid-value red">{semaforo.durata_rosso}s</span>
                  </div>
                  <div className="tlgw-grid-item">
                    <span className="tlgw-grid-label">Lungh. Attr.</span>
                    <span className="tlgw-grid-value">{semaforo.lunghezza_attraversamento}m</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Intervalli di Onda Verde (renamed from INTERVALLI) */}
        {finalResult?.intervals && finalResult.intervals.length > 0 && (
          <div className="tlgw-results-section">
            <h4 className="tlgw-results-section-title">Intervalli di Onda Verde</h4>
            <div className="tlgw-results-intervals">
              {finalResult.intervals.map((interval, i) => (
                <div key={i} className="tlgw-results-interval-tag">
                  Intervallo {i + 1}: {interval[0].toFixed(2)} – {interval[1].toFixed(2)}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ControlloOndaVerdeCard;
